<script setup lang="ts">
/* =============================================================
   DASHBOARD
   =============================================================
   The landing screen after sign-in: how much is on the platform,
   and the way in to each list.

   Every count is a HEAD request (see countBerths /
   countCompanies / countVessels) and they run in parallel, so
   the whole screen is five round trips that transfer no rows.
   They are fired with allSettled rather than Promise.all so one
   failing table does not blank out the others' figures — a
   berths outage should not hide the company count.
   ============================================================= */

import { onMounted, ref } from 'vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import StatLink from '@/components/ui/StatLink.vue'
import Alert from '@/components/ui/Alert.vue'
import Button from '@/components/ui/Button.vue'
import { routes } from '@/config/routes'
import { countBerths } from '@/lib/berths'
import { countCompanies } from '@/lib/companies'
import { countVessels } from '@/lib/vessels'
import { countBookings, countPendingBookings } from '@/lib/bookings'
import { DataError } from '@/lib/errors'
import { useAuth } from '@/lib/auth'

const { admin } = useAuth()

const berths = ref<number | null>(null)
const vessels = ref<number | null>(null)
const companies = ref<number | null>(null)
const bookings = ref<number | null>(null)
const pending = ref<number | null>(null)
const loading = ref(true)
const errors = ref<string[]>([])

function reason(e: unknown, fallback: string): string {
  return e instanceof DataError ? e.message : fallback
}

async function load(): Promise<void> {
  loading.value = true
  errors.value = []

  const [b, v, c, bk, p] = await Promise.allSettled([
    countBerths(),
    countVessels(),
    countCompanies(),
    countBookings(),
    countPendingBookings(),
  ])

  if (b.status === 'fulfilled') berths.value = b.value
  else errors.value.push(reason(b.reason, 'Could not count berths.'))

  if (v.status === 'fulfilled') vessels.value = v.value
  else errors.value.push(reason(v.reason, 'Could not count vessels.'))

  if (c.status === 'fulfilled') companies.value = c.value
  else errors.value.push(reason(c.reason, 'Could not count companies.'))

  if (bk.status === 'fulfilled') bookings.value = bk.value
  else errors.value.push(reason(bk.reason, 'Could not count bookings.'))

  // Same table as the line above, so a failure there already said so.
  if (p.status === 'fulfilled') pending.value = p.value

  loading.value = false
}

onMounted(load)

/** Thousands separators, because 2000-odd berths is the expected end state. */
const fmt = new Intl.NumberFormat('en-GB')
</script>

<template>
  <PageHeader
    title="Dashboard"
    :description="admin ? `Signed in as ${admin.name}. What is on the platform right now.` : undefined"
  >
    <template #actions>
      <Button variant="ghost" icon="refresh" :loading="loading" @click="load">Refresh</Button>
    </template>
  </PageHeader>

  <Alert v-for="message in errors" :key="message" tone="danger" class="page-alert">
    {{ message }}
  </Alert>

  <div class="tiles">
    <StatLink
      label="Berths"
      :value="berths === null ? '—' : fmt.format(berths)"
      :to="routes.berths"
      icon="anchor"
      note="Docking positions"
      :loading="loading && berths === null"
    />
    <StatLink
      label="Vessels"
      :value="vessels === null ? '—' : fmt.format(vessels)"
      :to="routes.vessels"
      icon="ship"
      note="Registered ships"
      :loading="loading && vessels === null"
    />
    <StatLink
      label="Companies"
      :value="companies === null ? '—' : fmt.format(companies)"
      :to="routes.companies"
      icon="building"
      note="Owners and operators"
      :loading="loading && companies === null"
    />
    <StatLink
      label="Bookings"
      :value="bookings === null ? '—' : fmt.format(bookings)"
      :to="routes.bookings"
      icon="bookings"
      :note="pending ? `${fmt.format(pending)} awaiting an owner` : 'Requests and confirmations'"
      :loading="loading && bookings === null"
    />
  </div>
</template>

<style scoped>
.tiles {
  display: grid;
  gap: var(--space-sm);
  /* The tiles fill the content width: auto-fit collapses the empty tracks
     and the 1fr hands what is left to the tiles that exist, so they stay
     equal at any width. Two up, then stacked, once 15rem each stops
     fitting. No max-width — a capped row of three left a gap on the right
     that read as a missing fourth tile. */
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
}
</style>
